import { ART_HEIGHT, ART_WIDTH } from "../schedule-art/art-canvas";
import { registerImage } from "../schedule-history/image-registry";

export type BuilderArtwork = {
  base: string;
  single: string[];
  double: string[];
  offline: string[];
  missingOffline: string[];
  sourceWidth: number;
  sourceHeight: number;
};

export type ArtworkIssue =
  | { code: "unsupported" | "name" | "decode" | "normalize"; file: string }
  | { code: "duplicate"; file: string; name: string }
  | { code: "missing"; names: string[] }
  | { code: "dimensions"; file: string; width: number; height: number; expectedWidth: number; expectedHeight: number };

type SourceFile = { name: string; file: File };
type DecodedFile = SourceFile & { image: HTMLImageElement };
type DecodeResult = { source: DecodedFile; issue?: never } | { issue: ArtworkIssue; source?: never };

export function artworkDayNames(prefix: string) {
  return Array.from({ length: 7 }, (_, index) => `${prefix}0${index + 1}`);
}

async function decodeSource(source: SourceFile): Promise<DecodeResult> {
  let url: string | undefined;
  try {
    const header = new Uint8Array(await source.file.slice(0, 12).arrayBuffer());
    const png = [137, 80, 78, 71, 13, 10, 26, 10].every((value, index) => header[index] === value);
    const webp = [82, 73, 70, 70].every((value, index) => header[index] === value)
      && [87, 69, 66, 80].every((value, index) => header[index + 8] === value);
    if (!png && !webp) return { issue: { code: "unsupported", file: source.file.name } };
    url = URL.createObjectURL(source.file);
    const image = new Image();
    image.src = url;
    await image.decode();
    if (!image.naturalWidth || !image.naturalHeight) throw new Error("Empty artwork");
    return { source: { ...source, image } };
  } catch {
    return { issue: { code: "decode", file: source.file.name } };
  } finally {
    if (url) URL.revokeObjectURL(url);
  }
}

async function normalizeImage(image: HTMLImageElement) {
  const canvas = document.createElement("canvas");
  canvas.width = ART_WIDTH;
  canvas.height = ART_HEIGHT;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Canvas unavailable");
  context.imageSmoothingQuality = "high";
  const scale = Math.min(ART_WIDTH / image.naturalWidth, ART_HEIGHT / image.naturalHeight);
  const width = image.naturalWidth * scale;
  const height = image.naturalHeight * scale;
  context.drawImage(image, (ART_WIDTH - width) / 2, (ART_HEIGHT - height) / 2, width, height);
  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(value => value ? resolve(value) : reject(new Error("Could not normalize artwork")), "image/png");
  });
  canvas.width = 0;
  canvas.height = 0;
  const url = URL.createObjectURL(blob);
  const normalized = new Image();
  try {
    normalized.src = url;
    await normalized.decode();
    return normalized;
  } catch (error) {
    URL.revokeObjectURL(url);
    throw error;
  }
}

export async function prepareBuilderArtwork(files: File[]): Promise<{ artwork: BuilderArtwork | null; issues: ArtworkIssue[] }> {
  const issues: ArtworkIssue[] = [];
  const sources = new Map<string, SourceFile>();
  files.forEach(file => {
    if (!/\.(png|webp)$/i.test(file.name)) {
      issues.push({ code: "unsupported", file: file.name });
      return;
    }
    const match = /^(base|[abc]0[1-7])\.(png|webp)$/i.exec(file.name);
    if (!match) {
      issues.push({ code: "name", file: file.name });
      return;
    }
    const name = match[1]!.toLowerCase();
    const previous = sources.get(name);
    if (previous) {
      issues.push({ code: "duplicate", file: `${previous.file.name}, ${file.name}`, name });
      return;
    }
    sources.set(name, { name, file });
  });
  const required = ["base", ...artworkDayNames("a"), ...artworkDayNames("b")];
  const missing = required.filter(name => !sources.has(name));
  if (missing.length) issues.push({ code: "missing", names: missing });
  const results = await Promise.all([...sources.values()].map(decodeSource));
  const decoded = results.flatMap(result => result.source ? [result.source] : []);
  results.forEach(result => { if (result.issue) issues.push(result.issue); });
  const reference = decoded.find(source => source.name === "base") ?? decoded[0];
  if (reference) {
    decoded.forEach(source => {
      const width = source.image.naturalWidth;
      const height = source.image.naturalHeight;
      const expectedWidth = reference.image.naturalWidth;
      const expectedHeight = reference.image.naturalHeight;
      if (width === expectedWidth && height === expectedHeight) return;
      issues.push({ code: "dimensions", file: source.file.name, width, height, expectedWidth, expectedHeight });
    });
  }
  if (issues.length || !reference) return { artwork: null, issues };

  // Register only after the entire batch succeeds; invalid sets never replace the preview.
  const normalized = await Promise.allSettled(decoded.map(source => normalizeImage(source.image)));
  normalized.forEach((result, index) => {
    if (result.status === "rejected") issues.push({ code: "normalize", file: decoded[index]!.file.name });
  });
  if (issues.length) {
    normalized.forEach(result => { if (result.status === "fulfilled") URL.revokeObjectURL(result.value.src); });
    return { artwork: null, issues };
  }
  const ids = new Map<string, string>();
  normalized.forEach((result, index) => {
    if (result.status === "fulfilled") ids.set(decoded[index]!.name, registerImage(result.value));
  });
  const single = artworkDayNames("a").map(name => ids.get(name)!);
  const double = artworkDayNames("b").map(name => ids.get(name)!);
  const offlineNames = artworkDayNames("c");
  const offline = offlineNames.map((name, index) => ids.get(name) ?? single[index]!);
  const artwork: BuilderArtwork = {
    base: ids.get("base")!, single, double, offline,
    missingOffline: offlineNames.filter(name => !ids.has(name)),
    sourceWidth: reference.image.naturalWidth, sourceHeight: reference.image.naturalHeight,
  };
  // Registered blob URLs stay alive for the session, including history's undone uploads.
  return { artwork, issues: [] };
}
